import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { AppComponent } from 'src/app/app.component';
import { IoService } from '../../../services/io/io.service';
import { ClientSettingsService } from '../../../services/client-settings/client-settings.service';
import {
  CorrectionDirection,
  CorrectionWording,
  buildWording,
  directionFor,
  effectSentence,
  entryText,
  formatQuantity,
  statement,
} from './correction-wording';

// Add Correction: adjusts a mistaken transaction toward a target value via an
// additional, clearly-marked correction transaction (POST /transaction/addCorrection).
// Opened from detail-transaction and from View component actions ('add-correction').
//
// The dialog never shows the user a signed number to edit. It states what the
// system recorded in words ("Sold 1 unit"), then asks what really happened as a
// direction picker plus an unsigned count ("Sold" / "Returned by customer" ×
// 2). The signed target is derived from those two (see correction-wording.ts)
// and sent as before; the server math is unchanged.
@Component({
  selector: 'app-correction-dialog',
  templateUrl: './correction-dialog.component.html',
  styleUrls: ['./correction-dialog.component.scss']
})
export class CorrectionDialogComponent implements OnInit {
  transaction: any = null;
  corrections: any[] = [];
  wording: CorrectionWording = null;
  loading = true;
  loadError: string = null;

  requireNote = false;
  // The user's answer: which way it went, and how many (always unsigned).
  direction: CorrectionDirection = null;
  countInput = '';
  note = '';
  confirming = false;
  saving = false;
  errorMessage: string = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<CorrectionDialogComponent>,
    private ioService: IoService,
    private clientSettingsService: ClientSettingsService,
  ) { }

  async ngOnInit() {
    this.requireNote = this.clientSettingsService.isCorrectionsRequireNote();
    try {
      const transaction: any = await this.ioService.post('/transaction/getTransaction', {
        transactionId: this.data.documentId,
      });
      this.transaction = transaction;
      this.corrections = transaction.corrections || [];
      this.wording = buildWording(transaction, transaction.correctionLedger);
      // Start from what the system currently says so the user edits the
      // number they see rather than typing from a blank.
      this.direction = directionFor(this.wording, this.effectiveRaw);
      this.countInput = this.effectiveRaw === 0 ? '' : this.magnitudeText(this.effectiveRaw);
      if (!transaction.canCorrect) {
        this.loadError = 'This transaction cannot be corrected.';
      }
    } catch (e) {
      this.loadError = e?.error?.msg || 'Failed to load transaction.';
    }
    this.loading = false;
  }

  get isMoney(): boolean {
    return this.transaction?.type !== 'inventory';
  }

  get divisor(): number {
    return this.transaction?.correctionDivisor || 1;
  }

  // Original + prior corrections, in raw stored units.
  get effectiveRaw(): number {
    if (!this.transaction) { return 0; }
    return (this.transaction.amount || 0)
      + this.corrections.reduce((sum, c) => sum + (c.amount || 0), 0);
  }

  // Unsigned display magnitude of a raw value, as text for the input.
  private magnitudeText(raw: number): string {
    const value = Math.abs(raw) / this.divisor;
    return this.isMoney ? value.toFixed(this.wording.precision) : String(Math.round(value * 10000) / 10000);
  }

  // The unsigned count the user typed, or null when blank / not a number.
  get count(): number | null {
    const text = (this.countInput || '').trim();
    if (!text) { return null; }
    const value = Math.abs(parseFloat(text));
    return isNaN(value) ? null : value;
  }

  // Signed target in raw stored units: direction carries the sign.
  get targetRaw(): number | null {
    if (this.count == null || !this.direction) { return null; }
    return Math.round(this.direction.sign * this.count * this.divisor * 10000) / 10000;
  }

  get deltaRaw(): number | null {
    if (this.targetRaw == null) { return null; }
    return Math.round((this.targetRaw - this.effectiveRaw) * 10000) / 10000;
  }

  // ── Sentences ──────────────────────────────────────────────────────────

  // "Sold 1 unit" — what the original transaction recorded.
  originalStatement(): string {
    return this.wording ? statement(this.wording, this.transaction.amount || 0, this.divisor) : '';
  }

  // The record as it stands now, after any prior corrections.
  currentStatement(): string {
    return this.wording ? statement(this.wording, this.effectiveRaw, this.divisor) : '';
  }

  // Prior corrections shown as the state each one left behind, not as deltas.
  priorStatements(): Array<{ text: string; userName: string; time: any }> {
    if (!this.wording) { return []; }
    let running = this.transaction.amount || 0;
    return this.corrections.map(c => {
      running += c.amount || 0;
      return { text: statement(this.wording, running, this.divisor), userName: c.userName, time: c.time };
    });
  }

  // "Sold 2 units" — what the user says really happened.
  targetStatement(): string {
    if (!this.wording || this.targetRaw == null) { return ''; }
    return this.direction.label + ' ' + formatQuantity(this.wording, this.count);
  }

  effect(): string {
    return this.wording && this.deltaRaw ? effectSentence(this.wording, this.deltaRaw, this.divisor) : '';
  }

  entry(): string {
    return this.wording && this.deltaRaw ? entryText(this.wording, this.deltaRaw, this.divisor) : '';
  }

  // True when the user is changing which way it went (a sale becoming a
  // return, a receipt becoming a return to the vendor). Worth a second look.
  flipsDirection(): boolean {
    return !!this.wording && this.effectiveRaw !== 0 && this.targetRaw != null && this.targetRaw !== 0
      && Math.sign(this.targetRaw) !== Math.sign(this.effectiveRaw);
  }

  unitLabel(): string {
    if (!this.wording || this.wording.kind !== 'inventory') { return ''; }
    return this.count === 1 ? 'unit' : 'units';
  }

  // ── Input handling ─────────────────────────────────────────────────────

  // The count is unsigned by construction: signs and exponents never enter.
  countKeyDown(event: KeyboardEvent) {
    if (['-', '+', 'e', 'E'].includes(event.key)) {
      event.preventDefault();
    }
  }

  // ngModel hands a number for type="number" inputs; keep digits and a point.
  countChanged(value: string | number) {
    this.countInput = String(value ?? '').replace(/[^0-9.]/g, '');
  }

  // Money shows as typed while editing, then settles to cents on blur.
  countBlur() {
    if (this.isMoney && this.count != null && this.wording) {
      this.countInput = this.count.toFixed(this.wording.precision);
    }
  }

  selectDirection(index: number) {
    this.direction = this.wording.directions[index] || this.direction;
  }

  canContinue(): boolean {
    return !!this.transaction?.canCorrect
      && this.deltaRaw != null
      && this.deltaRaw !== 0
      && (!this.requireNote || !!this.note.trim());
  }

  continueToConfirm() {
    if (this.canContinue()) {
      this.errorMessage = null;
      this.confirming = true;
    }
  }

  back() {
    this.confirming = false;
  }

  async submit() {
    if (this.saving || this.targetRaw == null) { return; }
    this.saving = true;
    this.errorMessage = null;
    try {
      await this.ioService.post('/transaction/addCorrection', {
        transactionId: this.transaction._id,
        targetValue: this.targetRaw / this.divisor,
        note: this.note.trim() || null,
      });
      AppComponent.showSnack('Correction added.', 'OK', 3000);
      this.dialogRef.close({ corrected: true });
    } catch (e) {
      this.errorMessage = e?.error?.msg || 'Correction failed.';
      this.confirming = false;
    }
    this.saving = false;
  }

  close() {
    this.dialogRef.close();
  }
}
